/**
 * LiteAPI hotels client — mirrors iOS HotelsAPI (search, filters, reservations).
 */

import { API_BASE_URL, buildAuthHeaders } from './apiClient';

function hotelsBase(): string {
  return API_BASE_URL.replace(/\/$/, '');
}

export interface LiteAPIFilterOption {
  id: string;
  name: string;
}

export interface LiteAPIFilterRange {
  min: number;
  max: number;
}

export interface LiteAPIFilterCatalog {
  amenities: LiteAPIFilterOption[];
  propertyTypes: LiteAPIFilterOption[];
  chains?: LiteAPIFilterOption[];
  stars: LiteAPIFilterRange;
  price: LiteAPIFilterRange;
  guestRating: LiteAPIFilterRange;
  refundableSupported: boolean;
}

export interface LiteAPISearchFilters {
  starsMin?: number;
  starsMax?: number;
  priceMin?: number;
  priceMax?: number;
  amenities?: string[];
  chains?: string[];
  propertyTypes?: string[];
  refundableOnly?: boolean;
  guestRatingMin?: number;
}

export interface HotelSearchOffer {
  offerId: string;
  hotelId: string;
  name: string;
  roomName?: string | null;
  stars?: number | null;
  rating?: number | null;
  reviewCount?: number | null;
  propertyType?: string | null;
  isHotel?: boolean;
  chain?: string | null;
  brand?: string | null;
  currency?: string | null;
  price?: number | null;
  totalPriceInclusive?: number | null;
  mealPlan?: string | null;
  cancellation?: string | null;
  photoUrl?: string | null;
  photoUrls?: string[] | null;
  formattedAddress?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  amenities?: string[] | null;
  provider?: string | null;
  roomId?: string | null;
  bookingKey?: string | null;
}

export interface SearchHotelsRequest {
  /** Display label only (city or "City — stay area"). Not the hotel-name query. */
  destination?: string;
  /**
   * Hotel name or fragment. Omitted when empty so the search stays geo-only.
   */
  searchTerm?: string;
  checkIn: string;
  checkOut: string;
  latitude: number;
  longitude: number;
  radiusKm?: number;
  adults?: number;
  cityVisitId?: string;
  limit?: number;
  offset?: number;
  currency?: string;
  countryCode?: string;
  filters?: LiteAPISearchFilters;
}

export interface SearchHotelsResponse {
  cityVisitId?: string;
  offers: HotelSearchOffer[];
}

export interface CreateHotelReservationRequest {
  offerId: string;
  name: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  countryCode: string;
  gender: 'MALE' | 'FEMALE' | 'OTHER';
  phone: string;
  isBusinessTrip: boolean;
  redirectUrlAfterPayment: string;
  address?: string;
  comment?: string;
}

export interface HotelReservationResponse {
  reservationId?: string | null;
  paymentUrl?: string | null;
  status?: string | null;
  confirmationNumber?: string | null;
}

async function readError(response: Response): Promise<string> {
  try {
    const err = await response.json();
    if (err?.detail) return String(err.detail);
    if (err?.title) return String(err.title);
    if (err?.message) return String(err.message);
  } catch {
    /* ignore */
  }
  return `HTTP ${response.status}`;
}

/** ISO date-time at UTC midnight for civil YYYY-MM-DD — matches HotelsAPI searchStayDates spirit. */
export function hotelSearchIsoDate(ymd: string): string {
  const day = ymd.slice(0, 10);
  return `${day}T00:00:00Z`;
}

export function offerDisplayPrice(offer: HotelSearchOffer): number | null {
  const v = offer.totalPriceInclusive ?? offer.price;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export async function fetchHotelFilters(): Promise<LiteAPIFilterCatalog> {
  const headers = await buildAuthHeaders();
  const response = await fetch(`${hotelsBase()}/hotels/filters`, {
    method: 'GET',
    headers,
  });
  if (!response.ok) throw new Error(await readError(response));
  const data = await response.json();
  if (data?.filters && typeof data.filters === 'object') {
    return data.filters as LiteAPIFilterCatalog;
  }
  return data as LiteAPIFilterCatalog;
}

/** Request body for POST /hotels/search. Empty `searchTerm` is omitted. */
export function buildHotelSearchBody(
  body: SearchHotelsRequest
): Record<string, unknown> {
  const { searchTerm, ...rest } = body;
  const term = searchTerm?.trim() ?? '';
  return {
    ...rest,
    ...(term ? { searchTerm: term } : {}),
    checkIn: hotelSearchIsoDate(body.checkIn),
    checkOut: hotelSearchIsoDate(body.checkOut),
    adults: body.adults ?? 2,
    limit: body.limit ?? 12,
    offset: body.offset ?? 0,
  };
}

export async function searchHotels(
  body: SearchHotelsRequest
): Promise<SearchHotelsResponse> {
  const headers = await buildAuthHeaders(true);
  const payload = buildHotelSearchBody(body);
  const response = await fetch(`${hotelsBase()}/hotels/search`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(await readError(response));
  const data = await response.json();
  return {
    cityVisitId: data.cityVisitId,
    offers: Array.isArray(data.offers) ? data.offers : [],
  };
}

export async function createHotelReservation(
  body: CreateHotelReservationRequest
): Promise<HotelReservationResponse> {
  const headers = await buildAuthHeaders(true);
  const response = await fetch(`${hotelsBase()}/hotels/reservations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as HotelReservationResponse;
}

export async function getHotelReservation(
  reservationId: string
): Promise<HotelReservationResponse> {
  const headers = await buildAuthHeaders();
  const response = await fetch(
    `${hotelsBase()}/hotels/reservations/${encodeURIComponent(reservationId)}`,
    { method: 'GET', headers }
  );
  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as HotelReservationResponse;
}
